import React, { useEffect } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { Stack, FormControlLabel, Switch, MenuItem } from "@mui/material";
import {
  Dialog,
  HookTextField,
  HookDatePicker,
  Text,
  COLORS,
} from "@asayinc/component-library";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import {
  IAdditionalMailingRequest,
  IAdditionalMailingRequestForm,
  IAdditionalMailingRequestPayload,
} from "src/store/additionalMailingRequest/types";
import {
  AdditionalMailingType,
  AdditionalMailingAudience,
} from "src/store/additionalMailing/types";
import { getErrorMessage } from "src/utils/isErrorWithMessage";
import {
  useCreateAdditionalMailingRequestMutation,
  useUpdateAdditionalMailingRequestMutation,
} from "src/store/additionalMailingRequest/api";
import {
  ADDITIONAL_MAILING_TYPE_OPTIONS,
  ADDITIONAL_MAILING_AUDIENCE_OPTIONS,
} from "src/constants/options/additionalMailingOptions";

interface CreateUpdateAdditionalMailingRequestDialogProps {
  open: boolean;
  onClose: () => void;
  proxyEventId: string;
  request: IAdditionalMailingRequest | null;
}

const getDefaultFormValues = (): IAdditionalMailingRequestForm => ({
  type: "",
  audience: "",
  minimumShares: "",
  sendDate: null,
  sendEmailNotifications: true,
  sendInAppNotifications: false,
});

export function CreateUpdateAdditionalMailingRequestDialog({
  open,
  onClose,
  proxyEventId,
  request,
}: CreateUpdateAdditionalMailingRequestDialogProps) {
  const [createAdditionalMailingRequest, { isLoading: isCreating }] =
    useCreateAdditionalMailingRequestMutation();
  const [updateAdditionalMailingRequest, { isLoading: isUpdating }] =
    useUpdateAdditionalMailingRequestMutation();

  const isEditing = !!request;
  const isLoading = isCreating || isUpdating;

  const form = useForm<IAdditionalMailingRequestForm>({
    defaultValues: getDefaultFormValues(),
  });

  const { handleSubmit, reset, control } = form;

  useEffect(() => {
    if (request) {
      reset({
        type: request.type,
        audience: request.audience,
        minimumShares: request.minimumShares,
        sendDate: request.sendDate ? dayjs(request.sendDate) : null,
        sendEmailNotifications: request.sendEmailNotifications,
        sendInAppNotifications: request.sendInAppNotifications,
      });
    } else {
      reset(getDefaultFormValues());
    }
  }, [request, reset]);

  const onSubmit = (data: IAdditionalMailingRequestForm) => {
    const payload: IAdditionalMailingRequestPayload = {
      type: data.type as AdditionalMailingType,
      audience: data.audience as AdditionalMailingAudience,
      minimumShares: Number(data.minimumShares),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sendDate: data.sendDate!.format("YYYY-MM-DD"),
      sendEmailNotifications: data.sendEmailNotifications,
      sendInAppNotifications: data.sendInAppNotifications,
    };

    if (isEditing && request) {
      updateAdditionalMailingRequest({ proxyEventId, id: request.id, payload })
        .unwrap()
        .then(() => {
          toast.success("Request updated successfully");
          onClose();
        })
        .catch((e) => {
          toast.error(getErrorMessage(e));
        });
    } else {
      createAdditionalMailingRequest({ proxyEventId, payload })
        .unwrap()
        .then(() => {
          toast.success("Request created successfully");
          onClose();
        })
        .catch((e) => {
          toast.error(getErrorMessage(e));
        });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        isEditing
          ? "Edit Additional Mailing Request"
          : "Create Additional Mailing Request"
      }
      content={
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={4}>
              <Text variant="body3" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
                {isEditing
                  ? "Update the details for this additional mailing request."
                  : "Create a new additional mailing request for this proxy event."}
              </Text>

              <HookTextField
                name="type"
                label="Type"
                select
                fullWidth
                rules={{ required: "Type is required" }}
              >
                {ADDITIONAL_MAILING_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </HookTextField>

              <HookTextField
                name="audience"
                label="Audience"
                select
                fullWidth
                rules={{ required: "Audience is required" }}
              >
                {ADDITIONAL_MAILING_AUDIENCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </HookTextField>

              <HookTextField
                name="minimumShares"
                label="Minimum Shares"
                type="number"
                fullWidth
                InputProps={{
                  inputProps: { min: 0 },
                  onWheel: (e: React.WheelEvent) =>
                    (e.target as HTMLElement).blur(),
                }}
                rules={{
                  required: "Minimum shares is required",
                  min: {
                    value: 0,
                    message: "Minimum shares must be at least 0",
                  },
                }}
              />

              <HookDatePicker
                name="sendDate"
                rules={{ required: "Send date is required" }}
                dateFieldProps={{
                  disablePast: true,
                  slotProps: {
                    textField: {
                      fullWidth: true,
                      outerLabel: "Send Date",
                      outerLabelVariant: "body3",
                    },
                  },
                }}
              />

              <Controller
                name="sendEmailNotifications"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Send Email Notifications"
                  />
                )}
              />

              <Controller
                name="sendInAppNotifications"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Send In-App Notifications"
                  />
                )}
              />
            </Stack>
          </form>
        </FormProvider>
      }
      buttonActions={[
        {
          label: "Cancel",
          onClick: onClose,
          variant: "secondary",
          size: "small",
          disabled: isLoading,
        },
        {
          label: isLoading ? "Saving..." : isEditing ? "Update" : "Create",
          onClick: handleSubmit(onSubmit),
          loading: isLoading,
          size: "small",
        },
      ]}
      ctaOrientation="horizontal"
    />
  );
}
